import React, { useState, useEffect } from 'react';
import { api, useLanguage } from '@/lib/context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Share2, TrendingUp, MousePointerClick, BarChart3,
  Facebook, Linkedin, MessageCircle, Mail, Twitter, Copy
} from 'lucide-react';
import { toast } from 'sonner';

// Platform icons mapping
const PLATFORM_ICONS = {
  facebook: Facebook,
  linkedin: Linkedin,
  whatsapp: MessageCircle,
  messenger: MessageCircle,
  twitter: Twitter,
  tiktok: Share2,
  email: Mail,
  copy: Copy
};

// Platform display names - brand names, no translation needed
const PLATFORM_NAMES = {
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  twitter: 'Twitter/X',
  tiktok: 'TikTok',
  email: 'Email',
  copy: 'Copy Link'
};

// Entity type display names - using i18n keys
const ENTITY_NAME_KEYS = {
  job: 'shareAnalytics.entities.jobs',
  service: 'shareAnalytics.entities.services',
  rental: 'shareAnalytics.entities.rentals',
  auto_service: 'shareAnalytics.entities.autoServices',
  subcontract: 'shareAnalytics.entities.subcontracting'
};

// Conversion type display names - using i18n keys
const CONVERSION_NAME_KEYS = {
  registration: 'shareAnalytics.conversions.registration',
  job_application: 'shareAnalytics.conversions.jobApplication',
  rental_request: 'shareAnalytics.conversions.rentalRequest',
  service_request: 'shareAnalytics.conversions.serviceRequest',
  subcontract_bid: 'shareAnalytics.conversions.subcontractBid',
  contact_unlock: 'shareAnalytics.conversions.contactUnlock'
};

// Simple bar chart component - accepts t function for translations
const SimpleBarChart = ({ data, labelKey, valueKey, maxBars = 8, t }) => {
  if (!data || data.length === 0) return <p className="text-sm text-muted-foreground">{t ? t('shareAnalytics.noData') : 'No data available'}</p>;
  
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  const sortedData = [...data].sort((a, b) => b[valueKey] - a[valueKey]).slice(0, maxBars);
  
  // Helper to get display name with translation
  const getDisplayName = (label) => {
    if (PLATFORM_NAMES[label]) return PLATFORM_NAMES[label];
    if (ENTITY_NAME_KEYS[label] && t) return t(ENTITY_NAME_KEYS[label]);
    if (CONVERSION_NAME_KEYS[label] && t) return t(CONVERSION_NAME_KEYS[label]);
    return label;
  };
  
  return (
    <div className="space-y-2">
      {sortedData.map((item, idx) => {
        const label = item[labelKey];
        const value = item[valueKey];
        const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
        const Icon = PLATFORM_ICONS[label];
        const displayName = getDisplayName(label);
        
        return (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-28 text-sm text-muted-foreground truncate flex items-center gap-1">
              {Icon && <Icon className="h-3 w-3" />}
              {displayName}
            </div>
            <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
              <div 
                className="h-full bg-brand/80 rounded transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="w-12 text-right text-sm font-medium">{value}</div>
          </div>
        );
      })}
    </div>
  );
};

// Mini trend line
const TrendLine = ({ data, dataKey }) => {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => d[dataKey]);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 100 - ((v - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" className="w-20 h-8" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        points={points}
        className="text-brand"
      />
    </svg>
  );
};

export const ShareAnalytics = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [conversionStats, setConversionStats] = useState(null);
  const [funnelStats, setFunnelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('shares'); // 'shares' | 'conversions' | 'funnel'

  useEffect(() => {
    fetchAllStats();
  }, [period]);

  const fetchAllStats = async () => {
    setLoading(true);
    try {
      const [shareRes, conversionRes, funnelRes] = await Promise.all([
        api.get(`/admin/shares/stats?days=${period}`),
        api.get(`/admin/conversions/stats?days=${period}`).catch(() => ({ data: null })),
        api.get(`/admin/conversions/funnel?days=${period}`).catch(() => ({ data: null }))
      ]);
      setStats(shareRes.data);
      setConversionStats(conversionRes.data);
      setFunnelStats(funnelRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="share-analytics-loading">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Format platform data for chart
  const platformData = stats?.by_platform 
    ? Object.entries(stats.by_platform).map(([platform, count]) => ({ platform, count }))
    : [];

  // Format entity data for chart  
  const entityData = stats?.by_entity_type
    ? Object.entries(stats.by_entity_type).map(([entity, count]) => ({ entity, count }))
    : [];

  // Format conversion data
  const conversionByTypeData = conversionStats?.by_type
    ? Object.entries(conversionStats.by_type).map(([type, count]) => ({ type, count }))
    : [];

  return (
    <Card className="mb-8" data-testid="share-analytics" id="share-analytics">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-brand" />
            {t('shareAnalytics.title')}
          </CardTitle>
          <CardDescription>{t('shareAnalytics.subtitle')}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {/* Tab Selector */}
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setActiveTab('shares')}
              className={`px-3 py-1 text-sm rounded ${activeTab === 'shares' ? 'bg-background shadow' : ''}`}
            >
              {t('shareAnalytics.tabs.shares')}
            </button>
            <button
              onClick={() => setActiveTab('conversions')}
              className={`px-3 py-1 text-sm rounded ${activeTab === 'conversions' ? 'bg-background shadow' : ''}`}
            >
              {t('shareAnalytics.tabs.conversions')}
            </button>
            <button
              onClick={() => setActiveTab('funnel')}
              className={`px-3 py-1 text-sm rounded ${activeTab === 'funnel' ? 'bg-background shadow' : ''}`}
            >
              {t('shareAnalytics.tabs.funnel')}
            </button>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('shareAnalytics.periods.last7days')}</SelectItem>
              <SelectItem value="30">{t('shareAnalytics.periods.last30days')}</SelectItem>
              <SelectItem value="90">{t('shareAnalytics.periods.last90days')}</SelectItem>
              <SelectItem value="365">{t('shareAnalytics.periods.lastYear')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Shares Tab */}
        {activeTab === 'shares' && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <Share2 className="h-5 w-5 text-muted-foreground" />
                  {stats?.daily_trend && <TrendLine data={stats.daily_trend} dataKey="shares" />}
                </div>
                <p className="text-2xl font-bold mt-2">{stats?.total_shares || 0}</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.totalShares')}</p>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <MousePointerClick className="h-5 w-5 text-muted-foreground" />
                  {stats?.daily_clicks && <TrendLine data={stats.daily_clicks} dataKey="clicks" />}
                </div>
                <p className="text-2xl font-bold mt-2">{stats?.total_clicks || 0}</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.linkClicks')}</p>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold mt-2">{stats?.click_rate || 0}%</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.clickRate')}</p>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <BarChart3 className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold mt-2">{platformData.length}</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.activeChannels')}</p>
              </div>
            </div>
            
            {/* Charts Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  {t('shareAnalytics.sharesByPlatform')}
                </h4>
                <SimpleBarChart data={platformData} labelKey="platform" valueKey="count" t={t} />
              </div>
              
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  {t('shareAnalytics.sharesByListingType')}
                </h4>
                <SimpleBarChart data={entityData} labelKey="entity" valueKey="count" t={t} />
              </div>
            </div>
            
            {/* Top Shared Listings */}
            {stats?.top_shared_listings?.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">{t('shareAnalytics.topSharedListings')}</h4>
                <div className="space-y-2">
                  {stats.top_shared_listings.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {ENTITY_NAME_KEYS[item.entity_type] ? t(ENTITY_NAME_KEYS[item.entity_type]) : item.entity_type}
                        </Badge>
                        <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {item.entity_id.substring(0, 8)}...
                        </span>
                      </div>
                      <Badge>{item.shares} shares</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Conversions Tab */}
        {activeTab === 'conversions' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-500/10 rounded-lg p-4">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <p className="text-2xl font-bold mt-2">{conversionStats?.total_conversions || 0}</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.totalConversions')}</p>
              </div>
              
              <div className="bg-brand/10 rounded-lg p-4">
                <Share2 className="h-5 w-5 text-brand" />
                <p className="text-2xl font-bold mt-2">{conversionStats?.conversions_from_shares || 0}</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.fromShares')}</p>
              </div>
              
              <div className="bg-blue-500/10 rounded-lg p-4">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <p className="text-2xl font-bold mt-2">{conversionStats?.conversion_rate || 0}%</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.conversionRate')}</p>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <MousePointerClick className="h-5 w-5 text-muted-foreground" />
                <p className="text-2xl font-bold mt-2">{conversionStats?.total_clicks || 0}</p>
                <p className="text-sm text-muted-foreground">{t('shareAnalytics.metrics.totalClicks')}</p>
              </div>
            </div>
            
            {/* Conversion Types */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">{t('shareAnalytics.conversionsByType')}</h4>
                {conversionByTypeData.length > 0 ? (
                  <SimpleBarChart data={conversionByTypeData} labelKey="type" valueKey="count" t={t} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t('shareAnalytics.noConversionData')}</p>
                )}
              </div>
              
              <div>
                <h4 className="font-medium mb-3">{t('shareAnalytics.conversionsByPlatform')}</h4>
                {conversionStats?.by_platform && Object.keys(conversionStats.by_platform).length > 0 ? (
                  <SimpleBarChart 
                    data={Object.entries(conversionStats.by_platform).map(([p, c]) => ({ platform: p, count: c }))} 
                    labelKey="platform" 
                    valueKey="count"
                    t={t}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{t('shareAnalytics.noPlatformData')}</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Funnel Tab */}
        {activeTab === 'funnel' && funnelStats && (
          <>
            {/* Funnel Visualization */}
            <div className="mb-6">
              <h4 className="font-medium mb-4">{t('shareAnalytics.conversionFunnel')}</h4>
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <div className="w-32 h-20 bg-brand/20 rounded-lg flex items-center justify-center">
                    <div>
                      <p className="text-2xl font-bold">{funnelStats.funnel?.shares || 0}</p>
                      <p className="text-xs text-muted-foreground">{t('shareAnalytics.tabs.shares')}</p>
                    </div>
                  </div>
                </div>
                <div className="text-muted-foreground">→</div>
                <div className="text-center">
                  <div className="w-28 h-18 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <div>
                      <p className="text-xl font-bold">{funnelStats.funnel?.clicks || 0}</p>
                      <p className="text-xs text-muted-foreground">{t('shareAnalytics.funnel.clicks')}</p>
                    </div>
                  </div>
                  <p className="text-xs mt-1 text-muted-foreground">{funnelStats.rates?.click_rate || 0}% {t('shareAnalytics.funnel.rate')}</p>
                </div>
                <div className="text-muted-foreground">→</div>
                <div className="text-center">
                  <div className="w-24 h-16 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <div>
                      <p className="text-lg font-bold">{funnelStats.funnel?.conversions || 0}</p>
                      <p className="text-xs text-muted-foreground">{t('shareAnalytics.tabs.conversions')}</p>
                    </div>
                  </div>
                  <p className="text-xs mt-1 text-muted-foreground">{funnelStats.rates?.conversion_rate || 0}% {t('shareAnalytics.funnel.rate')}</p>
                </div>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                {t('shareAnalytics.funnel.overall', { rate: funnelStats.rates?.overall_rate || 0 })}
              </p>
            </div>
            
            {/* Platform Performance */}
            {funnelStats.by_platform?.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">{t('shareAnalytics.platformPerformance')}</h4>
                <div className="space-y-2">
                  {funnelStats.by_platform.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded">
                      <div className="flex items-center gap-2">
                        {PLATFORM_ICONS[p.platform] && React.createElement(PLATFORM_ICONS[p.platform], { className: 'h-4 w-4' })}
                        <span className="font-medium">{PLATFORM_NAMES[p.platform] || p.platform}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span>{p.shares} {t('shareAnalytics.tabs.shares').toLowerCase()}</span>
                        <span className="text-green-600 font-medium">{p.conversions} {t('shareAnalytics.tabs.conversions').toLowerCase()}</span>
                        <Badge variant={p.conversion_rate > 5 ? 'default' : 'secondary'}>
                          {p.conversion_rate}% {t('shareAnalytics.funnel.rate')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ShareAnalytics;
